import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { CornerDownLeft } from 'lucide-react'
import { cn } from '@/lib/cn'
import { useT } from '@/i18n'
import { Dialog } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'

/**
 * Global qidiruv — ⌘K (TZ X4).
 *
 * HOZIRCHA faqat EKRAN navigatsiyasi. TZ X4 «foydalanuvchi, chat, bot, tiket,
 * ekran» deydi — obyekt qidiruvi OpenSearch'ni talab qiladi (TZ §0.3), u hali
 * yo'q. Obyektlar qo'shilguncha shu yerda ko'rsatilmaydi: yolg'on bo'sh
 * natija «topilmadi» degan noto'g'ri xulosaga olib keladi.
 */
export function CommandPalette({
  open,
  onClose,
  items,
}: {
  open: boolean
  onClose: () => void
  items: { to: string; label: string; icon: ReactNode }[]
}) {
  const t = useT()
  const navigate = useNavigate()
  const [q, setQ] = useState('')
  const [active, setActive] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (open) {
      setQ('')
      setActive(0)
    }
  }, [open])

  const results = useMemo(() => {
    const needle = q.trim().toLowerCase()
    if (!needle) return items
    return items.filter((i) => i.label.toLowerCase().includes(needle) || i.to.includes(needle))
  }, [q, items])

  useEffect(() => {
    setActive((a) => Math.min(a, Math.max(0, results.length - 1)))
  }, [results.length])

  function go(index: number) {
    const item = results[index]
    if (!item) return
    onClose()
    navigate(item.to)
  }

  return (
    <Dialog open={open} onClose={onClose} title={t.common.search} width="md" initialFocusRef={inputRef}>
      <div className="space-y-2">
        <Input
          ref={inputRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t.common.search}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault()
              setActive((a) => Math.min(a + 1, results.length - 1))
            } else if (e.key === 'ArrowUp') {
              e.preventDefault()
              setActive((a) => Math.max(a - 1, 0))
            } else if (e.key === 'Enter') {
              e.preventDefault()
              go(active)
            }
          }}
        />

        <ul className="max-h-80 overflow-auto">
          {results.length === 0 ? (
            <li className="px-2 py-6 text-center text-sm text-tertiary">{t.states.emptyTitle}</li>
          ) : (
            results.map((item, i) => (
              <li key={item.to}>
                <button
                  type="button"
                  onMouseEnter={() => setActive(i)}
                  onClick={() => go(i)}
                  className={cn(
                    'flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left text-base',
                    i === active ? 'bg-surface-3 text-primary' : 'text-secondary',
                  )}
                >
                  <span className="shrink-0 text-tertiary">{item.icon}</span>
                  <span className="flex-1 truncate">{item.label}</span>
                  <span className="font-mono text-xs text-tertiary">{item.to}</span>
                  {i === active && <CornerDownLeft size={12} className="text-tertiary" />}
                </button>
              </li>
            ))
          )}
        </ul>
      </div>
    </Dialog>
  )
}
